// PortalSettings — the candidate's Profile: six tabs, laid out to the Mercor
// pattern (page title, tab row, narrow left column naming the section, wide right
// column holding the form). Reuses the CRM idiom: .gtc-tabs/.gtc-tab for tabs,
// .gtr-editform > .gtc-field for inputs, .gtc-primary for buttons. New .gtps-*
// classes cover only layout the CRM has no equivalent of.
// No native <select> anywhere: fixed choices are pills, open-ended ones (country,
// timezone) are text inputs, which also beats a 400-item dropdown on a phone.
import { gtConfirm } from '../../../components/GtConfirm';
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../../../config';
import { useClerk } from '@clerk/clerk-react';
import { GtSelect, GtDatePicker } from '../../../components/GtControls';
import { COUNTRY_OPTIONS } from '../../../components/gtCountries';

const TABS = [
  { key: 'resume', label: 'Resume' },
  { key: 'location', label: 'Location & work authorisation' },
  { key: 'availability', label: 'Availability' },
  { key: 'preferences', label: 'Work preferences' },
  { key: 'communications', label: 'Communications' },
  { key: 'account', label: 'Account' },
];
const DAYS = [
  { n: 0, letter: 'S', name: 'Sunday' }, { n: 1, letter: 'M', name: 'Monday' },
  { n: 2, letter: 'T', name: 'Tuesday' }, { n: 3, letter: 'W', name: 'Wednesday' },
  { n: 4, letter: 'T', name: 'Thursday' }, { n: 5, letter: 'F', name: 'Friday' },
  { n: 6, letter: 'S', name: 'Saturday' },
];
const START_OPTIONS = ['Immediately', 'Within 2 weeks', 'Within a month', 'Within 3 months', 'Not currently looking'];
const HOBBY_OPTIONS = ['Football', 'Basketball', 'Cricket', 'Rugby', 'Running', 'Cycling', 'Swimming', 'Golf', 'Reading', 'Music', 'Cooking', 'Travel', 'Photography', 'Gaming'];
const LINK_KINDS = [
  { kind: 'github', label: 'GitHub username' },
  { kind: 'leetcode', label: 'LeetCode username' },
  { kind: 'kaggle', label: 'Kaggle username' },
  { kind: 'portfolio', label: 'Portfolio URL' },
];

export default function PortalSettings({ getToken, user, onChanged }) {
  const [tab, setTab] = useState('resume');
  const [state, setState] = useState('loading');
  const [profile, setProfile] = useState(null);
  const [sections, setSections] = useState(null);
  const [domains, setDomains] = useState([]);
  const [msg, setMsg] = useState(null);

  // Clear the banner itself. Leaving it pinned after the work finished made a
  // completed save look like it was still running.
  useEffect(() => {
    if (!msg) return undefined;
    // A refusal has to be read, a success only noticed.
    const t = setTimeout(() => setMsg(null), msg.kind === 'error' ? 7000 : 3500);
    return () => clearTimeout(t);
  }, [msg]);

  const authed = useCallback(async (path, options) => {
    const token = await getToken();
    const o = options || {};
    return fetch(API_BASE + path, { ...o, headers: { ...(o.headers || {}), Authorization: 'Bearer ' + token } });
  }, [getToken]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [pRes, sRes, dRes] = await Promise.all([
        authed('/api/portal/profile'), authed('/api/portal/profile/full'), authed('/api/portal/domains'),
      ]);
      if (pRes.status === 404) { setState('none'); return; }
      if (!pRes.ok || !sRes.ok) { setState('error'); return; }
      const p = await pRes.json();
      const s = await sRes.json();
      setProfile(p.profile); setSections(s.sections);
      if (dRes.ok) { const d = await dRes.json(); setDomains(d.domains || []); }
      setState('ready');
    } catch (e) { setState('error'); }
  }, [authed]);

  useEffect(() => { load(); }, [load]);

  const saveScalars = useCallback(async (patch) => {
    const r = await authed('/api/portal/profile', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.field ? 'Check the ' + String(d.field).replace(/_/g, ' ') + ' field.' : 'Could not save.');
    setProfile(d.profile);
  }, [authed]);

  const saveSection = useCallback(async (name, rows) => {
    const r = await authed('/api/portal/profile/sections/' + name, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error('Could not save ' + name + (d.field ? ' (' + String(d.field).replace(/_/g, ' ') + ')' : '') + '.');
  }, [authed]);

  const saveList = useCallback(async (name, items) => {
    const r = await authed('/api/portal/profile/lists/' + name, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }),
    });
    if (!r.ok) throw new Error('Could not save ' + name + '.');
  }, [authed]);

  const saveDomains = useCallback(async (slugs) => {
    const r = await authed('/api/portal/profile/domain-interests', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slugs }),
    });
    if (!r.ok) throw new Error('Could not save your domain interests.');
  }, [authed]);

  const attest = useCallback(async (body) => {
    const r = await authed('/api/portal/profile/attest', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('Could not save your attestation.');
  }, [authed]);

  const reload = useCallback(async () => { await load(); if (onChanged) onChanged(); }, [load, onChanged]);
  const api = { authed, saveScalars, saveSection, saveList, saveDomains, attest, setMsg, reload };

  if (state === 'loading') return <div className="gtr-card gtp2-state">Loading your profile</div>;
  if (state === 'error') return <div className="gtr-card gtp2-state">We could not load your profile just now. Try again shortly.</div>;
  if (state === 'none') {
    return (
      <div className="gtr-card gtr2-pad">
        <div className="gtr2-soon">
          <b>No profile yet</b>
          <p>Apply to a role and your profile will be created, ready for you to complete.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gtr-card gtr2-tabcard gtp2-settings">
      <div className="gtr2-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
            className={'gtr2-tab' + (tab === t.key ? ' gtr2-tab--on' : '')}
            onClick={() => { setTab(t.key); setMsg(null); }}>{t.label}</button>
        ))}
      </div>
      <div className="gtp2-settings-body">
      {/* Fixed, not inline. This page is long, and an alert at the top is
          invisible to someone acting on a control near the bottom. */}
      {msg && (
        <div
          className={'gtc-toast' + (msg.kind === 'error' ? ' gtc-toast--err' : '')}
          role="status"
          aria-live="polite"
        >
          {msg.text}
        </div>
      )}

      {tab === 'resume' && <ResumePane profile={profile} sections={sections} setSections={setSections} api={api} />}
      {tab === 'location' && <LocationPane profile={profile} api={api} />}
      {tab === 'availability' && <AvailabilityPane profile={profile} sections={sections} setSections={setSections} api={api} />}
      {tab === 'preferences' && <PreferencesPane profile={profile} sections={sections} setSections={setSections} domains={domains} api={api} />}
      {tab === 'communications' && <CommunicationsPane profile={profile} api={api} />}
      {tab === 'account' && <AccountPane profile={profile} user={user} api={api} />}
      </div>
    </div>
  );
}

/* layout primitives */

function Pane({ title, description, meta, children }) {
  return (
    <div className="gtps-pane">
      <aside className="gtps-aside">
        <h2 className="gtps-aside-h">{title}</h2>
        <p className="gtps-aside-p">{description}</p>
        {meta && <p className="gtps-aside-meta">{meta}</p>}
      </aside>
      <div className="gtps-body">{children}</div>
    </div>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <label className={'gtc-field' + (full ? ' gtps-field--full' : '')}>
      <span>{label}</span>
      {children}
      {hint && <small className="gtps-hint">{hint}</small>}
    </label>
  );
}

function Grid({ children }) { return <div className="gtps-grid">{children}</div>; }

function Block({ heading, sub, action, children }) {
  return (
    <section className="gtps-block">
      <div className="gtps-block-head">
        <h3 className="gtps-block-h">{heading}</h3>
        {action}
      </div>
      {sub && <p className="gtps-block-sub">{sub}</p>}
      {children}
    </section>
  );
}

function SaveRow({ dirty, saving, onSave }) {
  return (
    <div className="gtps-saverow">
      <button type="button" className="gtc-primary" disabled={!dirty || saving} onClick={onSave}>
        {saving ? 'Saving' : 'Save changes'}
      </button>
    </div>
  );
}

function Pills({ options, value, onChange, multi }) {
  const selected = multi ? (value || []) : [value];
  const toggle = (v) => {
    if (!multi) { onChange(value === v ? null : v); return; }
    const set = new Set(value || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange(Array.from(set));
  };
  return (
    <div className="gtps-pills">
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const on = selected.indexOf(v) >= 0;
        return (
          <button key={v} type="button" aria-pressed={on}
            className={'gtpt-fpill' + (on ? ' gtpt-fpill--on' : '')}
            onClick={() => toggle(v)}>{label}</button>
        );
      })}
    </div>
  );
}

function Chips({ items, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if ((items || []).some((x) => x.toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    onChange([...(items || []), v]);
    setDraft('');
  };
  return (
    <div className="gtps-chipbox">
      <div className="gtps-chiplist">
        {(items || []).map((s) => (
          <span key={s} className="gtps-chip">{s}
            <button type="button" aria-label={'Remove ' + s} onClick={() => onChange(items.filter((x) => x !== s))}>×</button>
          </span>
        ))}
      </div>
      <input type="text" value={draft} placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add} />
    </div>
  );
}

function ToggleRow({ title, sub, on, onChange }) {
  return (
    <div className="gtps-togglerow">
      <div className="gtps-togglerow-main">
        <div className="gtps-togglerow-h">{title}</div>
        {sub && <p className="gtps-togglerow-p">{sub}</p>}
      </div>
      <button type="button" role="switch" aria-checked={on} aria-label={title}
        className={'gtps-switch' + (on ? ' gtps-switch--on' : '')}
        onClick={() => onChange(!on)}><span className="gtps-switch-knob" /></button>
    </div>
  );
}

function Repeat({ heading, addLabel, rows, blank, titleOf, onChange, children }) {
  const set = (i, patch) => onChange(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  return (
    <Block heading={heading} action={
      <button type="button" className="gtps-addbtn" onClick={() => onChange([...rows, { ...blank }])}>
        <span aria-hidden="true">+</span> {addLabel}
      </button>
    }>
      {rows.length === 0 && <p className="gtps-empty">Nothing added yet.</p>}
      {rows.map((row, i) => (
        <div key={i} className="gtps-row">
          <div className="gtps-rowhead">
            <h4 className="gtps-rowtitle">{titleOf(row) || addLabel}</h4>
            <button type="button" className="gtps-removebtn" onClick={() => onChange(rows.filter((_, n) => n !== i))}>
              <span aria-hidden="true">×</span> Remove
            </button>
          </div>
          {children(row, (patch) => set(i, patch))}
        </div>
      ))}
    </Block>
  );
}

function usePane(initial) {
  const [form, setForm] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  return { form, dirty, setDirty, saving, setSaving, set };
}

/* Resume */

function ResumePane({ profile, sections, setSections, api }) {
  const p = usePane({ profile_summary: profile.profile_summary || '' });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const cv = profile.cv || {};

  // Uploading parses the CV and fills the sections below. Anything already
  // filled in is left alone, so this is safe to do more than once.
  const uploadCv = async (file) => {
    if (!file) return;
    setUploading(true); api.setMsg(null);
    try {
      const fd = new FormData();
      fd.append('cv', file);
      const r = await api.authed('/api/portal/cv', { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        api.setMsg({ kind: 'error', text: d.error === 'cv parse failed'
          ? 'We could not read that file. Try a different PDF or Word document.'
          : 'Could not upload your CV. Try again shortly.' });
        return;
      }
      api.setMsg({ kind: 'ok', text: 'CV uploaded. We have filled in what we could below.' });
      await api.reload();
    } catch (e) {
      api.setMsg({ kind: 'error', text: 'Could not upload your CV. Try again shortly.' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const [local, setLocal] = useState({
    education: sections.education || [], experience: sections.experience || [],
    projects: sections.projects || [], publications: sections.publications || [],
    certifications: sections.certifications || [], awards: sections.awards || [],
    languages: sections.languages || [], links: sections.links || [],
    skills: sections.skills || [], hobbies: sections.hobbies || [],
  });
  const setPart = (k, v) => { setLocal((s) => ({ ...s, [k]: v })); p.setDirty(true); };
  const linkValue = (kind) => { const f = (local.links || []).find((l) => l.kind === kind); return f ? f.value : ''; };
  const setLink = (kind, value) => {
    const others = (local.links || []).filter((l) => l.kind !== kind);
    setPart('links', value.trim() ? [...others, { kind, value: value.trim() }] : others);
  };

  const save = async () => {
    p.setSaving(true); api.setMsg(null);
    try {
      // In parallel. Eleven sequential round trips made Save look broken.
      await Promise.all([
        api.saveScalars({ profile_summary: p.form.profile_summary }),
        ...['education', 'experience', 'projects', 'publications', 'certifications', 'awards', 'languages', 'links']
          .map((n) => api.saveSection(n, local[n])),
        api.saveList('skills', local.skills),
        api.saveList('hobbies', local.hobbies),
      ]);
      setSections({ ...sections, ...local });
      p.setDirty(false);
      api.setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); } finally { p.setSaving(false); }
  };

  return (
    <Pane title="Resume" description="This is what employers see when we put you forward for a role.">
      <div className="gtr-editform">
        <section className={'gtc-card gtpt-cvcard' + (cv.has_cv ? '' : ' gtpt-cvcard--empty')}>
          <div className="gtpt-cvcard-main">
            <div className="gtpt-cvcard-h">Your CV</div>
            {cv.has_cv ? (
              <>
                <p className="gtpt-cvcard-file">{cv.filename}</p>
                <p className="gtpt-cvcard-sub">Updated {formatDate(cv.updated_at)}</p>
              </>
            ) : (
              <p className="gtpt-cvcard-sub">
                Start here. Upload your CV and we will fill in your history below for you to
                check and correct. You cannot apply for a role until we have it. PDF or Word,
                up to 10MB.
              </p>
            )}
          </div>
          <div className="gtpt-cvcard-actions">
            <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: 'none' }}
              onChange={(e) => uploadCv(e.target.files && e.target.files[0])} />
            <button type="button" className="gtc-primary" disabled={uploading}
              onClick={() => fileRef.current && fileRef.current.click()}>
              {uploading ? 'Reading your CV' : cv.has_cv ? 'Replace CV' : 'Upload your CV'}
            </button>
          </div>
        </section>

        <Block heading="Summary" sub="A short paragraph in your own words.">
          <Grid>
            <Field label="Summary" full>
              <textarea rows={4} value={p.form.profile_summary}
                onChange={(e) => p.set({ profile_summary: e.target.value })}
                placeholder="What you do, what you are good at, what you are looking for" />
            </Field>
          </Grid>
        </Block>

        <Repeat heading="Education" addLabel="Add education" rows={local.education}
          blank={{ school: '', degree: '', major: '', start_year: '', end_year: '', gpa: '' }}
          titleOf={(r) => (r.degree && r.school ? r.degree + ' from ' + r.school : r.school || r.degree)}
          onChange={(v) => setPart('education', v)}>
          {(row, set) => (
            <Grid>
              <Field label="School"><input type="text" value={row.school || ''} onChange={(e) => set({ school: e.target.value })} /></Field>
              <Field label="Degree"><input type="text" value={row.degree || ''} onChange={(e) => set({ degree: e.target.value })} /></Field>
              <Field label="Start year"><input type="text" inputMode="numeric" value={row.start_year || ''} onChange={(e) => set({ start_year: e.target.value })} /></Field>
              <Field label="End year"><input type="text" inputMode="numeric" value={row.end_year || ''} onChange={(e) => set({ end_year: e.target.value })} /></Field>
              <Field label="Subject"><input type="text" value={row.major || ''} onChange={(e) => set({ major: e.target.value })} /></Field>
              <Field label="Grade"><input type="text" value={row.gpa || ''} onChange={(e) => set({ gpa: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Repeat heading="Work experience" addLabel="Add work experience" rows={local.experience}
          blank={{ company: '', role_title: '', start_year: '', end_year: '', is_current: false, city: '', country: '', description: '' }}
          titleOf={(r) => (r.role_title && r.company ? r.role_title + ' at ' + r.company : r.company || r.role_title)}
          onChange={(v) => setPart('experience', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Company"><input type="text" value={row.company || ''} onChange={(e) => set({ company: e.target.value })} /></Field>
              <Field label="Role"><input type="text" value={row.role_title || ''} onChange={(e) => set({ role_title: e.target.value })} /></Field>
              <Field label="Start year"><input type="text" inputMode="numeric" value={row.start_year || ''} onChange={(e) => set({ start_year: e.target.value })} /></Field>
              <Field label="End year" hint="Leave blank if this is your current role">
                <input type="text" inputMode="numeric" value={row.end_year || ''}
                  onChange={(e) => set({ end_year: e.target.value, is_current: !e.target.value })} />
              </Field>
              <Field label="City"><input type="text" value={row.city || ''} onChange={(e) => set({ city: e.target.value })} /></Field>
              <Field label="Country">
                <GtSelect value={row.country || ''} options={COUNTRY_OPTIONS}
                  placeholder="Select a country" onChange={(v) => set({ country: v })} />
              </Field>
              <Field label="Description" full><textarea rows={5} value={row.description || ''} onChange={(e) => set({ description: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Repeat heading="Projects" addLabel="Add project" rows={local.projects}
          blank={{ name: '', url: '', description: '' }} titleOf={(r) => r.name}
          onChange={(v) => setPart('projects', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Name"><input type="text" value={row.name || ''} onChange={(e) => set({ name: e.target.value })} /></Field>
              <Field label="Link"><input type="text" value={row.url || ''} onChange={(e) => set({ url: e.target.value })} /></Field>
              <Field label="Description" full><textarea rows={3} value={row.description || ''} onChange={(e) => set({ description: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Repeat heading="Publications" addLabel="Add publication" rows={local.publications}
          blank={{ title: '', publisher: '', url: '', year: '' }} titleOf={(r) => r.title}
          onChange={(v) => setPart('publications', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Title" full><input type="text" value={row.title || ''} onChange={(e) => set({ title: e.target.value })} /></Field>
              <Field label="Publisher"><input type="text" value={row.publisher || ''} onChange={(e) => set({ publisher: e.target.value })} /></Field>
              <Field label="Year"><input type="text" inputMode="numeric" value={row.year || ''} onChange={(e) => set({ year: e.target.value })} /></Field>
              <Field label="Link" full><input type="text" value={row.url || ''} onChange={(e) => set({ url: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Repeat heading="Certifications" addLabel="Add certification" rows={local.certifications}
          blank={{ name: '', issuer: '', issued_year: '', credential_url: '' }} titleOf={(r) => r.name}
          onChange={(v) => setPart('certifications', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Name"><input type="text" value={row.name || ''} onChange={(e) => set({ name: e.target.value })} /></Field>
              <Field label="Issuer"><input type="text" value={row.issuer || ''} onChange={(e) => set({ issuer: e.target.value })} /></Field>
              <Field label="Year"><input type="text" inputMode="numeric" value={row.issued_year || ''} onChange={(e) => set({ issued_year: e.target.value })} /></Field>
              <Field label="Credential link"><input type="text" value={row.credential_url || ''} onChange={(e) => set({ credential_url: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Repeat heading="Awards" addLabel="Add award" rows={local.awards}
          blank={{ title: '', issuer: '', year: '', description: '' }} titleOf={(r) => r.title}
          onChange={(v) => setPart('awards', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Title"><input type="text" value={row.title || ''} onChange={(e) => set({ title: e.target.value })} /></Field>
              <Field label="Issuer"><input type="text" value={row.issuer || ''} onChange={(e) => set({ issuer: e.target.value })} /></Field>
              <Field label="Year"><input type="text" inputMode="numeric" value={row.year || ''} onChange={(e) => set({ year: e.target.value })} /></Field>
              <Field label="Description" full><textarea rows={2} value={row.description || ''} onChange={(e) => set({ description: e.target.value })} /></Field>
            </Grid>
          )}
        </Repeat>

        <Block heading="Profiles and links">
          <Grid>
            {LINK_KINDS.map((l) => (
              <Field key={l.kind} label={l.label}>
                <input type="text" value={linkValue(l.kind)} onChange={(e) => setLink(l.kind, e.target.value)} />
              </Field>
            ))}
          </Grid>
        </Block>

        <Block heading="Skills">
          <Chips items={local.skills} onChange={(v) => setPart('skills', v)} placeholder="Type a skill and press Enter" />
        </Block>

        <Repeat heading="Languages" addLabel="Add language" rows={local.languages}
          blank={{ language: '', proficiency: '' }} titleOf={(r) => r.language}
          onChange={(v) => setPart('languages', v)}>
          {(row, set) => (
            <Grid>
              <Field label="Language"><input type="text" value={row.language || ''} onChange={(e) => set({ language: e.target.value })} /></Field>
              <Field label="Proficiency" hint="For example: native, fluent, working">
                <input type="text" value={row.proficiency || ''} onChange={(e) => set({ proficiency: e.target.value })} />
              </Field>
            </Grid>
          )}
        </Repeat>

        <Block heading="Interests" sub="Select all that apply, or add your own.">
          <Pills multi options={HOBBY_OPTIONS} value={local.hobbies} onChange={(v) => setPart('hobbies', v)} />
          <div className="gtps-spacer">
            <Chips items={(local.hobbies || []).filter((h) => HOBBY_OPTIONS.indexOf(h) < 0)}
              onChange={(custom) => setPart('hobbies', [...(local.hobbies || []).filter((h) => HOBBY_OPTIONS.indexOf(h) >= 0), ...custom])}
              placeholder="Something else" />
          </div>
        </Block>
      </div>
      <SaveRow dirty={p.dirty} saving={p.saving} onSave={save} />
    </Pane>
  );
}

/* Location & work authorisation */

function LocationPane({ profile, api }) {
  const res = profile.location_residence || {};
  const wa = profile.work_authorisation || {};
  const p = usePane({
    residence_country: res.country || '', residence_region: res.region || '',
    residence_city: res.city || '', residence_postcode: res.postcode || '',
    works_from_other_country: Boolean(res.works_from_other_country),
    date_of_birth: res.date_of_birth || '',
  });
  const [authorised, setAuthorised] = useState(Boolean(wa.confirmed_at));
  const [willRemain, setWillRemain] = useState(Boolean(wa.remain_agreed_at));
  const country = p.form.residence_country || 'your country of residence';

  const save = async () => {
    p.setSaving(true); api.setMsg(null);
    try {
      await api.saveScalars(p.form);
      await api.attest({ authorised, will_remain: willRemain, country: p.form.residence_country });
      p.setDirty(false);
      api.setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); } finally { p.setSaving(false); }
  };

  return (
    <Pane title="Work authorisation" description="Where you are legally authorised to work."
      meta={wa.confirmed_at ? 'Last confirmed ' + formatDate(wa.confirmed_at) : null}>
      <div className="gtr-editform">
        <Block heading="Location of residence"
          sub="Where you are based for most of the year. This might be different from your citizenship.">
          <Grid>
            <Field label="Country">
              <GtSelect value={p.form.residence_country} options={COUNTRY_OPTIONS}
                placeholder="Select a country" onChange={(v) => p.set({ residence_country: v })} />
            </Field>
            <Field label="Region or county"><input type="text" value={p.form.residence_region} onChange={(e) => p.set({ residence_region: e.target.value })} /></Field>
            <Field label="City"><input type="text" value={p.form.residence_city} onChange={(e) => p.set({ residence_city: e.target.value })} /></Field>
            <Field label="Postcode"><input type="text" value={p.form.residence_postcode} onChange={(e) => p.set({ residence_postcode: e.target.value })} /></Field>
          </Grid>
          <label className="gtps-check">
            <input type="checkbox" checked={p.form.works_from_other_country}
              onChange={(e) => p.set({ works_from_other_country: e.target.checked })} />
            <span>I will be physically working from a different country than {country} while working through Generative Talent Co.</span>
          </label>
        </Block>

        <Block heading="Legal attestation" sub="Confirm your legally authorised work status.">
          <Grid>
            <Field label="Date of birth">
              <GtDatePicker value={p.form.date_of_birth} placeholder="Pick your date of birth"
                onChange={(v) => p.set({ date_of_birth: v })} />
            </Field>
          </Grid>
          <label className="gtps-check">
            <input type="checkbox" checked={authorised}
              onChange={(e) => { setAuthorised(e.target.checked); p.setDirty(true); }} />
            <span>I confirm that I am legally authorised to work from {country}.
              <small>You have the necessary visas, permits or legal rights to work from the country you have given.</small>
            </span>
          </label>
          <label className="gtps-check">
            <input type="checkbox" checked={willRemain}
              onChange={(e) => { setWillRemain(e.target.checked); p.setDirty(true); }} />
            <span>I agree to keep working from {country}, and to tell Generative Talent Co in writing before that changes.
              <small>You will obtain proper authorisation for any new country before working from it.</small>
            </span>
          </label>
        </Block>
      </div>
      <SaveRow dirty={p.dirty} saving={p.saving} onSave={save} />
    </Pane>
  );
}

/* Availability */

function AvailabilityPane({ profile, sections, setSections, api }) {
  const av = profile.availability || {};
  const p = usePane({
    availability_to_start: av.to_start || '',
    preferred_hours_per_week: av.hours_per_week == null ? '' : String(av.hours_per_week),
    timezone: av.timezone || guessTimezone(),
  });
  const [hours, setHours] = useState(sections.working_hours || []);
  const change = (v) => { setHours(v); p.setDirty(true); };

  const save = async () => {
    p.setSaving(true); api.setMsg(null);
    try {
      await api.saveScalars({
        availability_to_start: p.form.availability_to_start,
        preferred_hours_per_week: p.form.preferred_hours_per_week === '' ? null : p.form.preferred_hours_per_week,
        timezone: p.form.timezone,
      });
      await api.saveSection('working_hours', hours.map((h) => ({
        weekday: Number(h.weekday), start_time: h.start_time, end_time: h.end_time,
      })));
      setSections({ ...sections, working_hours: hours });
      p.setDirty(false);
      api.setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); } finally { p.setSaving(false); }
  };

  return (
    <Pane title="Availability" description="Set when you are typically available for work.">
      <div className="gtr-editform">
        <Block heading="Availability to start" sub="How soon you could begin a new role if offered.">
          <Pills options={START_OPTIONS} value={p.form.availability_to_start}
            onChange={(v) => p.set({ availability_to_start: v || '' })} />
          <Grid>
            <Field label="Preferred hours each week" hint="0 to 168">
              <input type="text" inputMode="numeric" value={p.form.preferred_hours_per_week}
                onChange={(e) => p.set({ preferred_hours_per_week: e.target.value })} />
            </Field>
            <Field label="Timezone" hint="Used to read your weekly hours">
              <input type="text" value={p.form.timezone} onChange={(e) => p.set({ timezone: e.target.value })} />
            </Field>
          </Grid>
        </Block>

        <Block heading="Working hours" sub="When you are typically available. Add more than one window for a split day.">
          <div className="gtps-hours">
            {DAYS.map((d) => {
              const windows = hours.filter((h) => Number(h.weekday) === d.n);
              return (
                <div key={d.n} className="gtps-hourday">
                  <span className="gtps-daymark" title={d.name} aria-label={d.name}>{d.letter}</span>
                  <div className="gtps-hourwins">
                    {windows.length === 0 && <span className="gtps-hourempty">Not available</span>}
                    {windows.map((w, i) => (
                      <div key={i} className="gtps-hourwin">
                        <input type="time" value={w.start_time ? String(w.start_time).slice(0, 5) : ''}
                          onChange={(e) => change(hours.map((h) => (h === w ? { ...h, start_time: e.target.value } : h)))}
                          aria-label={d.name + ' start'} />
                        <span aria-hidden="true">–</span>
                        <input type="time" value={w.end_time ? String(w.end_time).slice(0, 5) : ''}
                          onChange={(e) => change(hours.map((h) => (h === w ? { ...h, end_time: e.target.value } : h)))}
                          aria-label={d.name + ' end'} />
                        <button type="button" className="gtps-iconbtn" aria-label={'Remove ' + d.name + ' window'}
                          onClick={() => change(hours.filter((h) => h !== w))}>×</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="gtps-iconbtn" aria-label={'Add hours on ' + d.name}
                    onClick={() => change([...hours, { weekday: d.n, start_time: '09:00', end_time: '17:00' }])}>+</button>
                </div>
              );
            })}
          </div>
        </Block>
      </div>
      <SaveRow dirty={p.dirty} saving={p.saving} onSave={save} />
    </Pane>
  );
}

/* Work preferences */

function PreferencesPane({ profile, sections, setSections, domains, api }) {
  const pref = profile.preferences || {};
  const p = usePane({
    min_comp_ft_amount: pref.min_comp_ft_amount == null ? '' : String(pref.min_comp_ft_amount),
    min_comp_pt_amount: pref.min_comp_pt_amount == null ? '' : String(pref.min_comp_pt_amount),
    currency: pref.min_comp_ft_currency || 'GBP',
  });
  const [slugs, setSlugs] = useState((sections.domain_interests || []).map((d) => d.domain_slug));
  const [interests, setInterests] = useState(sections.interests || []);

  const save = async () => {
    p.setSaving(true); api.setMsg(null);
    try {
      await api.saveScalars({
        min_comp_ft_amount: p.form.min_comp_ft_amount === '' ? null : p.form.min_comp_ft_amount,
        min_comp_pt_amount: p.form.min_comp_pt_amount === '' ? null : p.form.min_comp_pt_amount,
        min_comp_ft_currency: p.form.currency, min_comp_pt_currency: p.form.currency,
      });
      await api.saveDomains(slugs);
      await api.saveList('interests', interests);
      setSections({ ...sections, interests, domain_interests: slugs.map((s) => ({ domain_slug: s })) });
      p.setDirty(false);
      api.setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); } finally { p.setSaving(false); }
  };

  return (
    <Pane title="Work preferences" description="Define how and when you would like to work.">
      <div className="gtr-editform">
        <Block heading="Domain interests"
          sub="What you are most interested in. We use this to tailor the roles we bring you.">
          <Pills multi value={slugs} onChange={(v) => { setSlugs(v); p.setDirty(true); }}
            options={domains.map((d) => ({ value: d.slug, label: d.label }))} />
          <div className="gtps-spacer">
            <Chips items={interests} onChange={(v) => { setInterests(v); p.setDirty(true); }}
              placeholder="Anything more specific, then Enter" />
          </div>
        </Block>

        <Block heading="Minimum expected pay"
          sub="We will not approach you about roles below this. It stays private and is never shown to employers.">
          <Grid>
            <Field label="Permanent, per year">
              <div className="gtps-money">
                <span>{p.form.currency}</span>
                <input type="text" inputMode="decimal" value={p.form.min_comp_ft_amount}
                  onChange={(e) => p.set({ min_comp_ft_amount: e.target.value })} placeholder="0" />
                <span>/ year</span>
              </div>
            </Field>
            <Field label="Contract, per hour">
              <div className="gtps-money">
                <span>{p.form.currency}</span>
                <input type="text" inputMode="decimal" value={p.form.min_comp_pt_amount}
                  onChange={(e) => p.set({ min_comp_pt_amount: e.target.value })} placeholder="0" />
                <span>/ hour</span>
              </div>
            </Field>
            <Field label="Currency" full hint="Applies to both figures above">
              <Pills options={['GBP', 'USD', 'EUR', 'CAD', 'AUD']} value={p.form.currency}
                onChange={(v) => p.set({ currency: v || 'GBP' })} />
            </Field>
          </Grid>
        </Block>
      </div>
      <SaveRow dirty={p.dirty} saving={p.saving} onSave={save} />
    </Pane>
  );
}

/* Communications */

function CommunicationsPane({ profile, api }) {
  const c = profile.communications || {};
  const p = usePane({
    looking_for_work: c.looking_for_work !== false, comm_email: c.email !== false,
    comm_sms: Boolean(c.sms), opp_fulltime: c.opp_fulltime !== false,
    opp_parttime: c.opp_parttime !== false, opp_contract: c.opp_contract !== false,
    notify_opportunities: c.notify_opportunities !== false,
    notify_status_updates: c.notify_status_updates !== false,
    unsubscribe_all: Boolean(c.unsubscribe_all),
  });
  const off = p.form.unsubscribe_all;

  const save = async () => {
    p.setSaving(true); api.setMsg(null);
    try {
      await api.saveScalars(p.form);
      p.setDirty(false);
      api.setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); } finally { p.setSaving(false); }
  };

  return (
    <Pane title="Communications" description="Choose how and where you hear from us.">
      <div className="gtps-togglegroup">
        <ToggleRow title="Looking for work" sub="Turn this off to pause role-related contact without leaving."
          on={p.form.looking_for_work} onChange={(v) => p.set({ looking_for_work: v })} />
      </div>

      <Block heading="Channels">
        <div className={'gtps-togglegroup' + (off ? ' gtps-togglegroup--off' : '')}>
          <ToggleRow title="Email" on={p.form.comm_email && !off} onChange={(v) => p.set({ comm_email: v })} />
          <ToggleRow title="Text message" on={p.form.comm_sms && !off} onChange={(v) => p.set({ comm_sms: v })} />
        </div>
      </Block>

      <Block heading="Opportunity types">
        <div className={'gtps-togglegroup' + (off ? ' gtps-togglegroup--off' : '')}>
          <ToggleRow title="Permanent roles" sub="Contact me about permanent positions"
            on={p.form.opp_fulltime && !off} onChange={(v) => p.set({ opp_fulltime: v })} />
          <ToggleRow title="Part-time roles" sub="Contact me about part-time positions"
            on={p.form.opp_parttime && !off} onChange={(v) => p.set({ opp_parttime: v })} />
          <ToggleRow title="Contract roles" sub="Contact me about contract and interim work"
            on={p.form.opp_contract && !off} onChange={(v) => p.set({ opp_contract: v })} />
        </div>
      </Block>

      <Block heading="Notifications">
        <div className={'gtps-togglegroup' + (off ? ' gtps-togglegroup--off' : '')}>
          <ToggleRow title="New opportunities" sub="New roles that match what you are looking for"
            on={p.form.notify_opportunities && !off} onChange={(v) => p.set({ notify_opportunities: v })} />
          <ToggleRow title="Application updates" sub="When one of your applications moves stage"
            on={p.form.notify_status_updates && !off} onChange={(v) => p.set({ notify_status_updates: v })} />
        </div>
        <div className="gtps-togglegroup gtps-togglegroup--warn">
          <ToggleRow title="Unsubscribe from everything"
            sub="Stops all outreach. We will still email you about an application you have already made."
            on={off} onChange={(v) => p.set({ unsubscribe_all: v })} />
        </div>
      </Block>
      <SaveRow dirty={p.dirty} saving={p.saving} onSave={save} />
    </Pane>
  );
}

/* Account */

function AccountPane({ profile, user, api }) {
  const email = profile.email || (user && user.primaryEmailAddress && user.primaryEmailAddress.emailAddress) || '\u2014';
  const { signOut } = useClerk();
  const [busy, setBusy] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [consent, setConsent] = useState((profile.consent || {}).status || null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api.authed('/api/portal/human-review');
        if (!r.ok) return;
        const d = await r.json();
        if (live) setRequests(d.requests || []);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { live = false; };
  }, [api]);

  const exportData = async () => {
    setBusy('export'); api.setMsg(null);
    try {
      const r = await api.authed('/api/portal/export');
      if (!r.ok) throw new Error('Could not build your export.');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gtco-' + (profile.ref || 'my') + '-data.json';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
      api.setMsg({ kind: 'ok', text: 'Your data has downloaded.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(null); }
  };

  const withdraw = async () => {
    const confirmed = await gtConfirm({
      title: 'Withdraw consent',
      message: [
        'We will stop using your information to match you to roles, and stop contacting you.',
        'Your account and your applications stay as they are.',
      ],
      confirmLabel: 'Withdraw consent',
      tone: 'danger',
    });
    if (!confirmed) return;
    setBusy('consent'); api.setMsg(null);
    try {
      const r = await api.authed('/api/portal/consent/withdraw', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      // The server explains refusals properly (a carve-out under impersonation,
      // for one). Surfacing its own words beats a generic failure line.
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || 'Could not withdraw consent.');
      setConsent('withdrawn');
      api.setMsg({ kind: 'ok', text: 'Consent withdrawn.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(null); }
  };

  const grantConsent = async () => {
    const confirmed = await gtConfirm({
      title: 'Give consent',
      message: [
        'We will start using your information to match you to roles, and may contact you about them.',
        'You can withdraw again at any time.',
      ],
      confirmLabel: 'Give consent',
    });
    if (!confirmed) return;
    setBusy('consent'); api.setMsg(null);
    try {
      const r = await api.authed('/api/portal/consent/grant', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || 'Could not record your consent.');
      setConsent('explicit');
      api.setMsg({ kind: 'ok', text: 'Consent given.' });
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(null); }
  };

  const askReview = async () => {
    setBusy('review'); api.setMsg(null);
    try {
      const r = await api.authed('/api/portal/human-review', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error('Could not raise your request.');
      setReason('');
      api.setMsg({
        kind: 'ok',
        text: d.already_open ? 'You already have a request open. A person will come back to you.'
          : 'Requested. A person will review this and come back to you.',
      });
      const list = await api.authed('/api/portal/human-review');
      if (list.ok) { const l = await list.json(); setRequests(l.requests || []); }
    } catch (e) { api.setMsg({ kind: 'error', text: e.message }); }
    finally { setBusy(null); }
  };

  const deleteAccount = async () => {
    setBusy('delete'); api.setMsg(null);
    try {
      const r = await api.authed('/api/portal/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: confirmText.trim() }),
      });
      // A refusal here is not always a typo. Deletion is blocked outright under
      // impersonation, and the server says so in plain words.
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || 'Could not delete your account. Check you typed DELETE.');
      try { await signOut(); } catch (e) { /* sign out is best effort */ }
      window.location.href = '/';
    } catch (e) {
      api.setMsg({ kind: 'error', text: e.message });
      setBusy(null);
    }
  };

  const openReq = requests.filter((x) => x.status !== 'closed');

  return (
    <Pane title="Account" description="Your sign-in, your data and your rights.">
      <div className="gtr-editform">
        <Block heading="Sign-in">
          <Grid>
            <Field label="Email" full hint="Managed by your sign-in. Change it from the account menu at the foot of the sidebar.">
              <input type="email" value={email} readOnly disabled />
            </Field>
          </Grid>
          <div className="gtr-ccard gtr-ccard-teal gtps-notecard">
            <div className="gtr-ccard-h">Member since</div>
            <p className="gtr-ccard-empty">{formatDate(profile.member_since)}. Your reference is {profile.ref}.</p>
          </div>
        </Block>

        <Block heading="How we use your information">
          <div className="gtr-ccard gtr-ccard-blue gtps-notecard">
            <p className="gtr-ccard-empty">
              Generative Talent Co (GTCO Ltd) is the controller of the information you give us. We
              process it, including with artificial intelligence, to match you to roles and to put you
              in front of employers. A person always makes the final decision about whether you are
              put forward.
            </p>
          </div>
        </Block>

        <Block heading="Get a copy of your data"
          sub="Everything you have given us, plus the record of what happened to it, as a file.">
          <div className="gtps-actionrow">
            <button type="button" className="gtc-primary" disabled={busy === 'export'} onClick={exportData}>
              {busy === 'export' ? 'Preparing' : 'Download my data'}
            </button>
          </div>
        </Block>

        <Block heading="Ask a person to review a decision"
          sub="Where a decision about you involved artificial intelligence, you can ask for a human to look again. You do not have to give a reason.">
          {requests.map((v) => (
            <div key={v.id}
              className={'gtr-ccard gtps-notecard ' + (v.status === 'closed' ? 'gtr-ccard-green' : 'gtr-ccard-amber')}>
              <div className="gtr-ccard-h">
                {v.status === 'closed' ? 'Reviewed' : v.status === 'in_review' ? 'Being reviewed now' : 'Requested'}
              </div>
              <p className="gtr-ccard-empty">
                Raised {formatDate(v.created_at)}{v.application_ref ? ', about ' + v.application_ref : ''}.
                {v.status === 'open' && ' A person will pick this up.'}
                {v.status === 'in_review' && ' Someone is looking at it now.'}
                {v.status === 'closed' && v.resolved_at ? ' Reviewed ' + formatDate(v.resolved_at) + '.' : ''}
              </p>
              {v.status === 'closed' && (
                <p className="gtr-ccard-empty gtps-outcome">
                  <b>What we decided.</b>{' '}
                  {v.resolution_note
                    || 'A person reviewed this. Contact us if you would like more detail.'}
                </p>
              )}
            </div>
          ))}
          {openReq.length === 0 && (
            <>
              <Grid>
                <Field label="Anything you want us to know" full>
                  <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Optional" />
                </Field>
              </Grid>
              <div className="gtps-actionrow">
                <button type="button" className="gtc-primary" disabled={busy === 'review'} onClick={askReview}>
                  {busy === 'review' ? 'Sending' : 'Request human review'}
                </button>
              </div>
            </>
          )}
        </Block>

        <Block
          heading={consent === 'withdrawn' ? 'Give consent' : 'Withdraw consent'}
          sub={consent === 'withdrawn'
            ? 'Consent is withdrawn. Give it again and we can start matching you to roles and contacting you.'
            : 'We stop using your information to match you to roles and stop contacting you. Your account and applications stay as they are.'}>
          {consent === 'withdrawn' ? (
            <>
              <div className="gtr-ccard gtr-ccard-amber gtps-notecard">
                <p className="gtr-ccard-empty">
                  Consent is withdrawn. Nothing is being used to match you to roles.
                </p>
              </div>
              <div className="gtps-actionrow">
                <button type="button" className="gtc-primary" disabled={busy === 'consent'} onClick={grantConsent}>
                  {busy === 'consent' ? 'Saving' : 'Give consent'}
                </button>
              </div>
            </>
          ) : (
            <div className="gtps-actionrow">
              <button type="button" className="gtps-warnbtn" disabled={busy === 'consent'} onClick={withdraw}>
                {busy === 'consent' ? 'Saving' : 'Withdraw consent'}
              </button>
            </div>
          )}
        </Block>

        <Block heading="Delete my account"
          sub="This removes your name, contact details, CV and everything on your profile. It cannot be undone. Type DELETE to confirm.">
          <Grid>
            <Field label="Type DELETE">
              <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE" autoComplete="off" />
            </Field>
          </Grid>
          <div className="gtps-actionrow">
            <button type="button" className="gtps-dangerbtn"
              disabled={busy === 'delete' || confirmText.trim().toUpperCase() !== 'DELETE'}
              onClick={deleteAccount}>
              {busy === 'delete' ? 'Deleting' : 'Delete my account'}
            </button>
          </div>
        </Block>
      </div>
    </Pane>
  );
}

function guessTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
}
function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch (e) { return ''; }
}
